// src/LandingPage.js - VelaTree branded version
import React from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Users, Heart, ArrowRight, LogIn, UserPlus, MessageCircle, Sparkle } from 'lucide-react';


const LandingPage = () => {
	return (
		<div className="min-h-screen flex flex-col bg-off-white">
			{/* Header */}
			<header className="bg-white shadow-sm">
				<div className="container mx-auto flex items-center justify-between px-6 py-4">
					<div className="flex items-center">
						<div className="bg-sage bg-opacity-20 rounded-full p-2 mr-3">
							<Leaf className="text-sage" size={24} />
						</div>
						<h1 className="text-2xl font-semibold text-charcoal">VelaTree</h1>
						<span className="ml-3 bg-sage bg-opacity-20 text-sage text-xs px-3 py-1 rounded-full font-medium">Beta</span>
					</div>
					<div className="flex items-center space-x-6">
						<Link 
							to="/login" 
							className="text-charcoal hover:text-sage flex items-center text-sm font-medium transition-colors"
						>
							<LogIn className="mr-1.5" size={16} />
							<span>Login</span>
						</Link>
						<Link 
							to="/register" 
							className="bg-coral text-white hover:bg-coral hover:bg-opacity-90 px-4 py-2 rounded-lg flex items-center text-sm font-medium transition-all"
						>
							<UserPlus className="mr-1.5" size={16} />
							<span>Get Started</span>
						</Link>
					</div>
				</div>
			</header>

			{/* Hero Section */}
			<section className="py-20 px-6">
				<div className="container mx-auto max-w-6xl">
					<div className="flex flex-col md:flex-row items-center justify-between gap-12">
						<div className="md:w-1/2">
							<h1 className="text-4xl md:text-5xl font-semibold text-charcoal mb-6 leading-tight">
								Find Your Perfect Therapist Match
							</h1>
							<p className="text-charcoal text-lg mb-8 leading-relaxed opacity-80">
								VelaTree uses thoughtful AI-powered insights to connect you with therapists who understand your unique communication style and therapeutic needs.
							</p>
							<div className="flex flex-col sm:flex-row gap-4">
								<Link
									to="/register"
									className="bg-sage text-white font-medium px-6 py-3.5 rounded-xl hover:bg-sage hover:bg-opacity-90 transition-all flex items-center justify-center shadow-sm"
								>
									<span>Begin Your Journey</span>
									<ArrowRight className="ml-2" size={18} />
								</Link>
							</div>
						</div>
						<div className="md:w-5/12">
							<div className="bg-white rounded-2xl p-8 shadow-lg border border-sage border-opacity-10">
								<div className="rounded-full bg-sage/30 bg-opacity-15 w-16 h-16 flex items-center justify-center mb-5">
									<MessageCircle className="text-sage" size={32} />
								</div>
								<h3 className="text-charcoal font-semibold text-xl mb-4">Thoughtful Matching Process</h3>
								<p className="text-charcoal opacity-70 mb-5 leading-relaxed">
									Create an account and engage with our gentle assessment. We'll understand your preferences and connect you with therapists whose approach aligns with your healing journey.
								</p>
								<Link
									to="/register"
									className="text-sage font-medium flex items-center hover:text-sage hover:opacity-80 transition-opacity"
								>
									<span>Start your assessment</span>
									<ArrowRight className="ml-1.5" size={16} />
								</Link>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Features Section */}
			<section className="py-20 px-6 bg-white">
				<div className="container mx-auto max-w-6xl">
					<h2 className="text-3xl font-semibold text-center text-charcoal mb-4">How VelaTree Works</h2>
					<p className="text-center text-charcoal opacity-70 max-w-2xl mx-auto mb-12">
						A gentle, evidence-based approach to finding the right therapeutic match
					</p>
					
					<div className="grid md:grid-cols-3 gap-8">
						{/* Feature 1 */}
						<div className="bg-off-white rounded-2xl p-8 border border-taupe border-opacity-30">
							<div className="rounded-full bg-sage/30 bg-opacity-15 w-14 h-14 flex items-center justify-center mb-5">
								<Users className="text-sage" size={26} />
							</div>
							<h3 className="text-lg font-semibold text-charcoal mb-3">Therapist Insights</h3>
							<p className="text-charcoal opacity-70 leading-relaxed">
								Built on wisdom from thousands of experienced therapists who understand what makes therapeutic relationships flourish.
							</p>
						</div>
						
						{/* Feature 2 */}
						<div className="bg-off-white rounded-2xl p-8 border border-taupe border-opacity-30">
							<div className="rounded-full bg-sage/30 bg-opacity-15 w-14 h-14 flex items-center justify-center mb-5">
								<Sparkle className="text-sage" size={26} />
							</div>
							<h3 className="text-lg font-semibold text-charcoal mb-3">Personalized Understanding</h3>
							<p className="text-charcoal opacity-70 leading-relaxed">
								Our gentle assessment explores your communication preferences and relationship patterns to create your unique therapeutic profile.
							</p>
						</div>
						
						{/* Feature 3 */}
						<div className="bg-off-white rounded-2xl p-8 border border-taupe border-opacity-30">
							<div className="rounded-full bg-sage/30 bg-opacity-15 w-14 h-14 flex items-center justify-center mb-5">
								<Heart className="text-sage" size={26} />
							</div>
							<h3 className="text-lg font-semibold text-charcoal mb-3">Meaningful Connections</h3>
							<p className="text-charcoal opacity-70 leading-relaxed">
								Get matched with therapists whose approach naturally resonates with you, creating space for genuine healing and growth.
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* CTA Section */}
			<section className="py-20 px-6 bg-sage bg-opacity-10">
				<div className="container mx-auto max-w-4xl text-center">
					<h2 className="text-3xl font-semibold text-charcoal mb-5">Ready to Find Your Therapeutic Match?</h2>
					<p className="text-charcoal opacity-80 max-w-2xl mx-auto mb-8 text-lg leading-relaxed">
						Take the first step towards finding a therapist who truly understands your unique needs and communication style.
					</p>
					<div className="flex justify-center">
						<Link
							to="/register"
							className="bg-sage text-white font-medium px-8 py-4 rounded-xl hover:bg-sage hover:bg-opacity-90 transition-all flex items-center justify-center shadow-md"
						>
							Begin Your Journey
						</Link>
					</div>
				</div>
			</section>

			{/* Team Section */}
			<section className="py-20 px-6 bg-white">
				<div className="container mx-auto max-w-6xl">
					<h2 className="text-3xl font-semibold text-center text-charcoal mb-4">Meet Our Team</h2>
					<p className="text-center text-charcoal opacity-70 max-w-2xl mx-auto mb-12">
						Dedicated professionals combining technology and psychology to improve mental health access
					</p>
					
					<div className="grid md:grid-cols-3 gap-8">
						{/* Team Member 1 */}
						<div className="rounded-2xl overflow-hidden shadow-md group relative h-96 border border-taupe border-opacity-20">
							{/* White overlay that fades on hover */}
							<div className="absolute inset-0 bg-white bg-opacity-60 transition-opacity duration-300 group-hover:opacity-0 z-10"></div>
							{/* Gradient for text readability */}
							<div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black/70 to-transparent z-10"></div>
							<div 
								className="w-full h-full bg-cover bg-center transition-transform duration-300 transform scale-110 translate-y-4 group-hover:translate-x-3"
								style={{ 
									backgroundImage: `url('/images/youssef.jpg')`,
									transformOrigin: 'center'
								}}
							></div>
							<div className="absolute bottom-0 left-0 right-0 p-6 text-white transition-transform duration-300 transform group-hover:-translate-y-6 z-20">
								<h3 className="text-lg font-semibold text-white">Youssef El Moumni</h3>
								<p className="text-taupe font-medium">Software Engineer | Founder</p>
								<div className="overflow-hidden h-0 group-hover:h-auto transition-all duration-300 opacity-0 group-hover:opacity-100 mt-2">
									<p className="text-gray-200 text-sm">
										Founder of 3 Startups, Full-Stack, Algorithmic Optimization, Real-Time Communications, VR
									</p>
								</div>
							</div>
						</div>
						
						{/* Team Member 2 */}
						<div className="rounded-2xl overflow-hidden shadow-md group relative h-96 border border-taupe border-opacity-20">
							{/* White overlay that fades on hover */}
							<div className="absolute inset-0 bg-white bg-opacity-60 transition-opacity duration-300 group-hover:opacity-0 z-10"></div>
							{/* Gradient for text readability */}
							<div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black/70 to-transparent z-10"></div>
							<div 
								className="w-full h-full bg-cover bg-center transition-transform duration-300 transform scale-110 translate-y-4 group-hover:translate-x-3"
								style={{ 
									backgroundImage: `url('/images/erik.jpg')`,
									transformOrigin: 'center'
								}}
							></div>
							<div className="absolute bottom-0 left-0 right-0 p-6 text-white transition-transform duration-300 transform group-hover:-translate-y-6 z-20">
								<h3 className="text-lg font-semibold text-white">Erik Knight</h3>
								<p className="text-taupe font-medium">Psychologist</p>
								<div className="overflow-hidden h-0 group-hover:h-auto transition-all duration-300 opacity-0 group-hover:opacity-100 mt-2">
									<p className="text-gray-200 text-sm">
										Management Consulting, Humanistic and Cognitive-Behavioral Consulting
									</p>
								</div>
							</div>
						</div>
						
						{/* Team Member 3 */}
						<div className="rounded-2xl overflow-hidden shadow-md group relative h-96 border border-taupe border-opacity-20">
							{/* White overlay that fades on hover */}
							<div className="absolute inset-0 bg-white bg-opacity-60 transition-opacity duration-300 group-hover:opacity-0 z-10"></div>
							{/* Gradient for text readability */}
							<div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black/70 to-transparent z-10"></div>
							<div 
								className="w-full h-full bg-cover bg-center transition-transform duration-300 transform scale-110 translate-y-4 group-hover:translate-x-3"
								style={{ 
									backgroundImage: `url('/images/david.jpg')`,
									transformOrigin: 'center'
								}}
							></div>
							<div className="absolute bottom-0 left-0 right-0 p-6 text-white transition-transform duration-300 transform group-hover:-translate-y-6 z-20">
								<h3 className="text-lg font-semibold text-white">Dr. David Stephens</h3>
								<p className="text-taupe font-medium">Neuropsychologist</p>
								<div className="overflow-hidden h-0 group-hover:h-auto transition-all duration-300 opacity-0 group-hover:opacity-100 mt-2">
									<p className="text-gray-200 text-sm">
										Dialectical Behavior Therapy, Supervision, Training
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Footer */}
			<footer className="bg-charcoal text-taupe py-10">
				<div className="container mx-auto px-6">
					<div className="flex flex-col md:flex-row justify-between items-center">
						<div className="flex items-center mb-4 md:mb-0">
							<div className="bg-sage bg-opacity-20 rounded-full p-2 mr-3">
								<Leaf className="text-sage" size={20} />
							</div>
							<h2 className="text-xl font-medium text-white">VelaTree</h2>
						</div>
						<div className="text-sm">
							Thoughtful therapy matching | Beta version
						</div>
					</div>
				</div>
			</footer>
		</div>
	);
};

export default LandingPage;